import React, { useState, useEffect } from 'react';
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  Activity,
  ChevronDown,
  ChevronRight,
  Play,
  DollarSign,
  Package,
  Database,
  BarChart3,
  Target,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from 'recharts';
import { useApi } from '../hooks/useApi';

const ProbabilisticModel = () => {
  const { fetchPost } = useApi();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate dates: 2 weeks back for start_date, tomorrow for end_date
  const getDefaultDates = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

    return {
      start: formatDate(twoWeeksAgo),
      end: formatDate(tomorrow)
    };
  };

  const defaultDates = getDefaultDates();

  const [formData, setFormData] = useState({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
    fix_i_model_spread: 5,
    base_volume: 30,
    base_fix_i_model: 'v2.0.0',
    fix_ii_model: 'v1.0.0-fix2',
    preds_diff_cutoff: 5,
    fix_i_model: 'v3.0.0',
    fix_ii_margin: 1000,
    bucket1_volume: 20,
    bucket2_volume: 40,
    bucket3_volume: 60
  });

  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [, setShowHourlyPerformance] = useState(false);
  const [hourlyPerformanceData, setHourlyPerformanceData] = useState([]);

  // Automatically calculate hourly performance when results are available
  useEffect(() => {
    if (!results || !results.results) {
      return;
    }

    const hourlyPnL = {};
    for (let i = 0; i < 24; i++) {
      hourlyPnL[i] = 0;
    }

    Object.values(results.results).forEach((dayData) => {
      if (dayData.hourly_results && Array.isArray(dayData.hourly_results)) {
        dayData.hourly_results.forEach((hourData) => {
          if (
            hourData.hourly_pnl !== null &&
            hourData.hourly_pnl !== undefined
          ) {
            hourlyPnL[hourData.hour] += hourData.hourly_pnl;
          }
        });
      }
    });

    const chartData = Object.entries(hourlyPnL).map(([hour, pnl]) => ({
      hour: displayHours1to24 ? parseInt(hour) + 1 : parseInt(hour),
      pnl: parseFloat(pnl.toFixed(2))
    }));

    setHourlyPerformanceData(chartData);
    setShowHourlyPerformance(true);
  }, [displayHours1to24, results]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const runCombinedAnalysis = async (overrideFormData = null) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setShowHourlyPerformance(false);
    setHourlyPerformanceData([]);

    try {
      const dataToUse = overrideFormData || formData;

      const formDataToSend = new FormData();
      Object.entries(dataToUse).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetchPost('/probabilistic-results', formDataToSend);

      const data = await response.json();

      if (data.status === 'success') {
        setResults(data);
      } else if (data.detail) {
        // Handle validation errors
        setError('Validation error: ' + JSON.stringify(data.detail));
      } else {
        setError(data.message || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error running probabilistic model analysis:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (date) => {
    setExpandedRows((prev) => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const renderBaseActionsTable = (data) => {
    const baseActionData = data.base_action_results || [];

    if (!data.base_action || data.base_action === 'none' || baseActionData.length === 0) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
          <p className="text-gray-500 text-sm">No base actions taken</p>
        </div>
      );
    }

    return (
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Base Actions</h3>
        <div className="mb-2 p-2 bg-gray-100 rounded">
          <span className="text-sm text-gray-700">
            Initial {data.base_action} of {data.base_volume} units at base price {formatNumber(data.base_price)} for all hours
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hour</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Model Pred</th>
                <th colSpan="5" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix I</th>
                <th colSpan="4" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Fix II</th>
                <th rowSpan="2" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">PnL</th>
                <th rowSpan="2" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Residual</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order Vol</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Executed</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l">Action</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Volume</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {baseActionData.map((row, idx) => {
                const residual = row.base_residual || 0;

                return (
                  <tr key={idx} className={row.base_pnl > 0 ? 'bg-green-50' : row.base_pnl < 0 ? 'bg-red-50' : ''}>
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {displayHours1to24 ? row.hour + 1 : row.hour}:00
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.fix_ii_prediction)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                      {row.base_fix_i_action && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.base_fix_i_action === 'buy' ? 'bg-green-200 text-green-800' :
                          row.base_fix_i_action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {row.base_fix_i_action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_i_order_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_i_volume || 0)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.fix_i_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {row.base_fix_i_volume > 0 ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center border-l">
                      {row.base_fix_ii_action && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          row.base_fix_ii_action === 'buy' ? 'bg-green-200 text-green-800' :
                          row.base_fix_ii_action === 'sell' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {row.base_fix_ii_action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_ii_order_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.fix_ii_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(row.base_fix_ii_volume || 0)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                      row.base_pnl > 0 ? 'text-green-600' : row.base_pnl < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {formatNumber(row.base_pnl)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {residual !== 0 && (
                        <span className="text-orange-600 font-medium">{residual}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderProbabilisticTable = (hourlyData) => {
    if (!hourlyData || hourlyData.length === 0)
      return (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Probabilistic Trading
          </h3>
          <p className="text-gray-500 text-sm">No trading data available</p>
        </div>
      );

    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Step Order Execution
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Hour
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Fix I Price
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Fix II Price
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q10
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q20
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q30
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q50
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q70
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q80
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Q90
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Fix I Action
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Volume
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Fix II Order
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Fix II Exec
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  PnL
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hourlyData.map((hour, idx) => {
                const fix_i_exec = hour.fix_i_execution || {};
                const fix_ii_exec = hour.fix_ii_execution || {};
                const quantiles = hour.quantiles || {};
                const pnl = hour.hourly_pnl || 0;

                // Determine which quantiles triggered
                const triggeredBuyQuantiles = [];
                const triggeredSellQuantiles = [];

                if (fix_i_exec.orders_executed) {
                  fix_i_exec.orders_executed.forEach((order) => {
                    if (fix_i_exec.action === 'buy') {
                      triggeredBuyQuantiles.push(order.quantile);
                    } else if (fix_i_exec.action === 'sell') {
                      triggeredSellQuantiles.push(order.quantile);
                    }
                  });
                }

                const getCellClass = (quantile, price, fixPrice) => {
                  if (triggeredBuyQuantiles.includes(quantile))
                    return 'bg-green-100 font-semibold';
                  if (triggeredSellQuantiles.includes(quantile))
                    return 'bg-red-100 font-semibold';

                  // Highlight potential triggers that didn't execute
                  if (quantile === 'q30' && fixPrice < price) return 'bg-green-50';
                  if (quantile === 'q20' && fixPrice < price) return 'bg-green-50';
                  if (quantile === 'q10' && fixPrice < price) return 'bg-green-50';
                  if (quantile === 'q70' && fixPrice > price) return 'bg-red-50';
                  if (quantile === 'q80' && fixPrice > price) return 'bg-red-50';
                  if (quantile === 'q90' && fixPrice > price) return 'bg-red-50';

                  return '';
                };

                // Check if no action was taken (no buy/sell action in Fix I)
                const noAction = !fix_i_exec.action || fix_i_exec.action === 'none';

                return (
                  <tr
                    key={idx}
                    className={
                      pnl > 0
                        ? 'bg-green-50'
                        : pnl < 0
                        ? 'bg-red-50'
                        : ''
                    }
                  >
                    <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {displayHours1to24 ? hour.hour + 1 : hour.hour}:00
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(hour.fix_i_price)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(hour.fix_ii_price)}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right ${getCellClass(
                        'q10',
                        quantiles.q10,
                        hour.fix_i_price
                      )}`}
                    >
                      {formatNumber(quantiles.q10)}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right ${getCellClass(
                        'q20',
                        quantiles.q20,
                        hour.fix_i_price
                      )}`}
                    >
                      {formatNumber(quantiles.q20)}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right ${getCellClass(
                        'q30',
                        quantiles.q30,
                        hour.fix_i_price
                      )}`}
                    >
                      {formatNumber(quantiles.q30)}
                    </td>
                    <td className={`px-2 py-2 whitespace-nowrap text-sm text-right ${
                      noAction ? 'bg-orange-50' : 'text-gray-600'
                    }`}>
                      {formatNumber(quantiles.q50)}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right ${getCellClass(
                        'q70',
                        quantiles.q70,
                        hour.fix_i_price
                      )}`}
                    >
                      {formatNumber(quantiles.q70)}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right ${getCellClass(
                        'q80',
                        quantiles.q80,
                        hour.fix_i_price
                      )}`}
                    >
                      {formatNumber(quantiles.q80)}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right ${getCellClass(
                        'q90',
                        quantiles.q90,
                        hour.fix_i_price
                      )}`}
                    >
                      {formatNumber(quantiles.q90)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {fix_i_exec.action && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            fix_i_exec.action === 'buy'
                              ? 'bg-green-200 text-green-800'
                              : fix_i_exec.action === 'sell'
                              ? 'bg-red-200 text-red-800'
                              : 'bg-gray-200 text-gray-800'
                          }`}
                        >
                          {fix_i_exec.action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {fix_i_exec.total_volume || '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                      {fix_ii_exec.order_price
                        ? formatNumber(fix_ii_exec.order_price)
                        : '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                      {fix_ii_exec.executed !== undefined &&
                        (fix_ii_exec.executed ? (
                          <span className="text-green-600 font-bold">✓</span>
                        ) : (
                          <span className="text-red-600 font-bold">✗</span>
                        ))}
                    </td>
                    <td
                      className={`px-2 py-2 whitespace-nowrap text-sm text-right font-semibold ${
                        pnl > 0
                          ? 'text-green-600'
                          : pnl < 0
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {formatNumber(pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }; // <-- FIX: close renderProbabilisticTable here

  const renderDateRow = (date, data) => {
    const isExpanded = expandedRows[date];
    const totalPnl = data.daily_pnl || 0;
    const basePnl = data.base_pnl || 0;
    const probabilisticPnl = data.probabilistic_pnl || 0;
    const totalVolume = data.total_volume_traded || 0;
    const isPnLPositive = totalPnl > 0;
    const isPnLNegative = totalPnl < 0;

    // Calculate average prices for display
    const getAvgFixPrices = () => {
      let avgFixI = 0;
      let avgFixII = 0;
      let count = 0;

      if (data.hourly_results) {
        data.hourly_results.forEach(hour => {
          avgFixI += hour.fix_i_price || 0;
          avgFixII += hour.fix_ii_price || 0;
          count++;
        });
      }

      return {
        avgFixI: count > 0 ? avgFixI / count : 0,
        avgFixII: count > 0 ? avgFixII / count : 0
      };
    };

    const { avgFixI, avgFixII } = getAvgFixPrices();

    return (
      <div key={date} className="border rounded-lg mb-4 overflow-hidden">
        <div
          className={`p-4 cursor-pointer transition-colors ${
            isPnLPositive ? 'bg-green-50 hover:bg-green-100' :
            isPnLNegative ? 'bg-red-50 hover:bg-red-100' :
            'bg-gray-50 hover:bg-gray-100'
          }`}
          onClick={() => toggleRow(date)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="font-semibold">{date}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                data.base_action === 'buy' ? 'bg-green-200 text-green-800' :
                data.base_action === 'sell' ? 'bg-red-200 text-red-800' :
                'bg-gray-200 text-gray-800'
              }`}>
                {data.base_action === 'none' ? 'idle' : data.base_action}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {Math.round(totalVolume)} MWh
              </span>
            </div>
            <div className="flex flex-col space-y-1 text-sm">
              {/* First row: Base, Model Pred, Fix I, Fix II, Preds Diff */}
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Base:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.base_price) || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Model Pred:</span>
                  <span className="ml-2 font-semibold">{formatNumber(data.avg_q50_prediction)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix I:</span>
                  <span className="ml-2 font-semibold">{formatNumber(avgFixI)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Fix II:</span>
                  <span className="ml-2 font-semibold">{formatNumber(avgFixII)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Preds Diff:</span>
                  <span className={`ml-2 font-semibold ${
                    data.base_action_skipped_by_preds_diff
                      ? (data.preds_diff > 0 ? 'text-red-600' : 'text-green-600')
                      : 'text-gray-900'
                  }`}>
                    {data.preds_diff !== null && data.preds_diff !== undefined
                      ? `${data.preds_diff > 0 ? '+' : ''}${data.preds_diff}%`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              {/* Second row: Base PnL, Probabilistic PnL, Daily PnL */}
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Base PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    basePnl > 0 ? 'text-green-600' :
                    basePnl < 0 ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {formatNumber(basePnl)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Arbitrage PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    probabilisticPnl > 0 ? 'text-green-600' :
                    probabilisticPnl < 0 ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {formatNumber(probabilisticPnl)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Daily PnL:</span>
                  <span className={`ml-2 font-semibold ${
                    totalPnl > 0 ? 'text-green-600' :
                    totalPnl < 0 ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {formatNumber(totalPnl)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="p-4 bg-white border-t">
            {/* BASE Actions Table */}
            {renderBaseActionsTable(data)}

            {/* Step Order Execution Table */}
            {renderProbabilisticTable(data.hourly_results)}

            {/* Daily Summary for this date */}
            {(() => {
              // Calculate daily metrics
              let buyHours = 0;
              let sellHours = 0;
              let totalBuyVolume = 0;
              let totalSellVolume = 0;
              let profitableHours = 0;
              if (data.hourly_results) {
                data.hourly_results.forEach((hour) => {
                  if (
                    hour.fix_i_execution &&
                    hour.fix_i_execution.action === 'buy'
                  ) {
                    buyHours++;
                    totalBuyVolume += hour.fix_i_execution.total_volume || 0;
                  } else if (
                    hour.fix_i_execution &&
                    hour.fix_i_execution.action === 'sell'
                  ) {
                    sellHours++;
                    totalSellVolume += hour.fix_i_execution.total_volume || 0;
                  }

                  if (hour.hourly_pnl > 0) {
                    profitableHours++;
                  }
                });
              }

              return (
                <div className="mt-6 border rounded-lg p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Daily Trading Summary
                  </h3>

                  {/* Trading Position Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="border rounded p-3 bg-white">
                      <div className="space-y-1 text-sm">
                        <div className="text-gray-600">Buy Hours</div>
                        <div className="text-xl font-bold text-green-600">
                          {buyHours}
                        </div>
                        <div className="text-xs text-gray-500">
                          Total: {totalBuyVolume} MW (Step Orders)
                        </div>
                      </div>
                    </div>

                    <div className="border rounded p-3 bg-white">
                      <div className="space-y-1 text-sm">
                        <div className="text-gray-600">Sell Hours</div>
                        <div className="text-xl font-bold text-red-600">
                          {sellHours}
                        </div>
                        <div className="text-xs text-gray-500">
                          Total: {totalSellVolume} MW (Step Orders)
                        </div>
                      </div>
                    </div>

                    <div className="border rounded p-3 bg-white">
                      <div className="space-y-1 text-sm">
                        <div className="text-gray-600">Profitable Hours</div>
                        <div className="text-xl font-bold text-green-600">
                          {profitableHours}
                        </div>
                        <div className="text-xs text-gray-500">
                          Win rate:{' '}
                          {buyHours + sellHours > 0
                            ? formatPercentage(
                                (profitableHours /
                                  (buyHours + sellHours)) *
                                  100
                              )
                            : 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className="border rounded p-3 bg-white">
                      <div className="space-y-1 text-sm">
                        <div className="text-gray-600">Daily PnL</div>
                        <div
                          className={`text-xl font-bold ${
                            totalPnl > 0
                              ? 'text-green-600'
                              : totalPnl < 0
                              ? 'text-red-600'
                              : 'text-gray-600'
                          }`}
                        >
                          {formatNumber(totalPnl)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Total: {Math.round(totalVolume)} MWh
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Probabilistic Model
          </h1>
          <p className="text-gray-600">Run probabilistic model analysis</p>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Activity size={16} />
                <span>Fix I Model Spread</span>
              </label>
              <input
                type="number"
                value={formData.fix_i_model_spread}
                onChange={(e) =>
                  handleInputChange(
                    'fix_i_model_spread',
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="1"
                placeholder="Default: 5"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Base Volume (MW)</span>
              </label>
              <input
                type="number"
                value={formData.base_volume}
                onChange={(e) =>
                  handleInputChange(
                    'base_volume',
                    parseInt(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="10"
                placeholder="Default: 30"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Database size={16} />
                <span>Base Fix I Model</span>
              </label>
              <select
                value={formData.base_fix_i_model}
                onChange={(e) => handleInputChange('base_fix_i_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Database size={16} />
                <span>Fix II Model</span>
              </label>
              <select
                value={formData.fix_ii_model}
                onChange={(e) => handleInputChange('fix_ii_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Preds Diff Cutoff (%)</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.preds_diff_cutoff}
                onChange={(e) => handleInputChange('preds_diff_cutoff', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Arbitrage Model</span>
              </label>
              <select
                value={formData.fix_i_model}
                onChange={(e) => handleInputChange('fix_i_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v3.0.0">v3.0.0</option>
                <option value="v3.0.1">v3.0.1</option>
                <option value="v5.0.0">v5.0.0</option>
                
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Target size={16} />
                <span>Fix II Margin</span>
              </label>
              <input
                type="number"
                value={formData.fix_ii_margin}
                onChange={(e) =>
                  handleInputChange(
                    'fix_ii_margin',
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="50"
                placeholder="Default: 500"
              />
            </div>
          </div>

          {/* Bucket Volume Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Bucket 1 Volume (MWh)</span>
              </label>
              <input
                type="number"
                value={formData.bucket1_volume}
                onChange={(e) =>
                  handleInputChange(
                    'bucket1_volume',
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="5"
                placeholder="Default: 20"
              />
              <p className="text-xs text-gray-500 mt-1">For q30/q70 orders</p>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Bucket 2 Volume (MWh)</span>
              </label>
              <input
                type="number"
                value={formData.bucket2_volume}
                onChange={(e) =>
                  handleInputChange(
                    'bucket2_volume',
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="5"
                placeholder="Default: 30"
              />
              <p className="text-xs text-gray-500 mt-1">Additional for q20/q80</p>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} />
                <span>Bucket 3 Volume (MWh)</span>
              </label>
              <input
                type="number"
                value={formData.bucket3_volume}
                onChange={(e) =>
                  handleInputChange(
                    'bucket3_volume',
                    parseFloat(e.target.value) || 0
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="5"
                placeholder="Default: 50"
              />
              <p className="text-xs text-gray-500 mt-1">Additional for q10/q90</p>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => runCombinedAnalysis()}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02]'
              }`}
            >
              <Play size={20} />
              <span>{isLoading ? 'Running Analysis...' : 'Run Analysis'}</span>
            </button>

            <button
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowStr = formatDate(tomorrow);
                const updatedFormData = {
                  ...formData,
                  start_date: tomorrowStr,
                  end_date: tomorrowStr
                };
                setFormData(updatedFormData);
                // Run analysis with the updated data
                runCombinedAnalysis(updatedFormData);
              }}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 transform hover:scale-[1.02]'
              }`}
            >
              <Calendar size={20} />
              <span>
                {isLoading ? 'Running Analysis...' : 'Run Analysis For Tomorrow'}
              </span>
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Results Summary */}
        {results && results.summary && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Summary</h2>

            {/* Key Metrics - First Row: Day Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Days</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {results.summary.total_days || 0}
                    </p>
                  </div>
                  <Calendar className="text-purple-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Profitable Days</p>
                    <p className="text-2xl font-bold text-green-800">
                      {results.summary.profitable_days || 0}
                    </p>
                    <p className="text-xs text-green-600">
                      {formatPercentage(results.summary.profitable_days_percentage)} of total
                    </p>
                  </div>
                  <TrendingUp className="text-green-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Loss Days</p>
                    <p className="text-2xl font-bold text-red-800">
                      {results.summary.loss_days || 0}
                    </p>
                    <p className="text-xs text-red-600">
                      {formatPercentage(results.summary.loss_days_percentage)} of total
                    </p>
                  </div>
                  <TrendingDown className="text-red-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Second Row: Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Buy Actions</p>
                    <p className="text-2xl font-bold text-emerald-800">
                      {results.summary.base_model_actions?.buy || 0}
                    </p>
                    <p className="text-xs text-emerald-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.buy)} of actions
                    </p>
                  </div>
                  <TrendingUp className="text-emerald-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-rose-600 font-medium">Sell Actions</p>
                    <p className="text-2xl font-bold text-rose-800">
                      {results.summary.base_model_actions?.sell || 0}
                    </p>
                    <p className="text-xs text-rose-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.sell)} of actions
                    </p>
                  </div>
                  <TrendingDown className="text-rose-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Idle Days</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {results.summary.base_model_actions?.none || 0}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatPercentage(results.summary.base_model_actions_percentages?.none)} of actions
                    </p>
                  </div>
                  <Activity className="text-gray-500" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Average Daily PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.avg_daily_combined_pnl >= 0 ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {formatNumber(results.summary.avg_daily_combined_pnl)}
                    </p>
                  </div>
                  <BarChart3 className="text-blue-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Third Row: PnL and Volume Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* 1. Total Base PnL */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Base PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_base_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_base_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-blue-500" size={32} />
                </div>
              </div>

              {/* 2. Total Fixing PnL */}
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600 font-medium">Total Fixing PnL</p>
                    <p className={`text-2xl font-bold ${
                      (results.summary.total_fixing_pnl || results.summary.total_probabilistic_pnl) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_fixing_pnl || results.summary.total_probabilistic_pnl)}
                    </p>
                  </div>
                  <Activity className="text-indigo-500" size={32} />
                </div>
              </div>

              {/* 3. Total Combined PnL */}
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-cyan-600 font-medium">Total Combined PnL</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.total_combined_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.total_combined_pnl)}
                    </p>
                  </div>
                  <DollarSign className="text-cyan-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Fourth Row: Volume and Efficiency Part 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* 4. Base Volume Traded */}
              <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-violet-600 font-medium">Base Volume Traded</p>
                    <p className="text-2xl font-bold text-violet-800">
                      {formatNumber(results.summary.total_base_volume_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-violet-600">
                      BASE contract volume executed
                    </p>
                  </div>
                  <Package className="text-violet-500" size={32} />
                </div>
              </div>

              {/* 5. Profit per MWh Traded in BASE */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Profit/MWh in BASE</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh_base >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh_base || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-purple-600">
                      BASE trading efficiency
                    </p>
                  </div>
                  <Target className="text-purple-500" size={32} />
                </div>
              </div>

              {/* 6. Arbitrage Volume Traded */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-teal-600 font-medium">Arbitrage Volume Traded</p>
                    <p className="text-2xl font-bold text-teal-800">
                      {formatNumber(results.summary.total_arbitrage_volume_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-teal-600">
                      Probabilistic/step orders volume
                    </p>
                  </div>
                  <Activity className="text-teal-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Fifth Row: Volume and Efficiency Part 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* 7. Profit per MWh Traded in Arbitrage */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Profit/MWh in Arbitrage</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh_arbitrage >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh_arbitrage || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-emerald-600">
                      Arbitrage trading efficiency
                    </p>
                  </div>
                  <Target className="text-emerald-500" size={32} />
                </div>
              </div>

              {/* 8. Total Volume Traded */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Total Volume Traded</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {formatNumber(results.summary.total_volume_traded_mwh || 0)} MWh
                    </p>
                    <p className="text-xs text-amber-600">
                      Combined BASE + Arbitrage
                    </p>
                  </div>
                  <Package className="text-amber-500" size={32} />
                </div>
              </div>

              {/* 9. Profit per MWh Traded (Overall) */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Profit per MWh Traded</p>
                    <p className={`text-2xl font-bold ${
                      results.summary.profit_per_mwh >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(results.summary.profit_per_mwh || 0)} PLN/MWh
                    </p>
                    <p className="text-xs text-orange-600">
                      Overall trading efficiency
                    </p>
                  </div>
                  <Target className="text-orange-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Sixth Row: Success Rate by Bucket */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Bucket 1: q30/q70 */}
              <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-lime-600 font-medium">Bucket 1 (q30/q70)</p>
                    <p className="text-2xl font-bold text-lime-800">
                      {results.summary.bucket_stats?.bucket1?.percentage || 0}%
                    </p>
                    <p className="text-xs text-lime-600">
                      {results.summary.bucket_stats?.bucket1?.profitable || 0} of {results.summary.bucket_stats?.bucket1?.total || 0} hours profitable
                    </p>
                  </div>
                  <TrendingUp className="text-lime-500" size={32} />
                </div>
              </div>

              {/* Bucket 2: q20/q80 */}
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-sky-600 font-medium">Bucket 2 (q20/q80)</p>
                    <p className="text-2xl font-bold text-sky-800">
                      {results.summary.bucket_stats?.bucket2?.percentage || 0}%
                    </p>
                    <p className="text-xs text-sky-600">
                      {results.summary.bucket_stats?.bucket2?.profitable || 0} of {results.summary.bucket_stats?.bucket2?.total || 0} hours profitable
                    </p>
                  </div>
                  <TrendingUp className="text-sky-500" size={32} />
                </div>
              </div>

              {/* Bucket 3: q10/q90 */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-pink-600 font-medium">Bucket 3 (q10/q90)</p>
                    <p className="text-2xl font-bold text-pink-800">
                      {results.summary.bucket_stats?.bucket3?.percentage || 0}%
                    </p>
                    <p className="text-xs text-pink-600">
                      {results.summary.bucket_stats?.bucket3?.profitable || 0} of {results.summary.bucket_stats?.bucket3?.total || 0} hours profitable
                    </p>
                  </div>
                  <TrendingUp className="text-pink-500" size={32} />
                </div>
              </div>
            </div>

            {/* Key Metrics - Seventh Row: Spread Analysis by Bucket */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {/* Bucket 1: q30/q70 Spreads */}
              <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-lime-600 font-medium">Bucket 1 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">When Right:</span>
                        <span className="text-base font-bold text-lime-800">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">When Wrong:</span>
                        <span className="text-base font-bold text-lime-800">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-lime-200">
                        <span className="text-xs text-lime-700">Volume:</span>
                        <span className="text-base font-bold text-lime-800">
                          {Math.round(results.summary.bucket_stats?.bucket1?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-lime-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-lime-900">
                          {formatNumber(results.summary.bucket_stats?.bucket1?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-lime-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-lime-500" size={32} />
                </div>
              </div>

              {/* Bucket 2: q20/q80 Spreads */}
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-sky-600 font-medium">Bucket 2 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">When Right:</span>
                        <span className="text-base font-bold text-sky-800">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">When Wrong:</span>
                        <span className="text-base font-bold text-sky-800">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-sky-200">
                        <span className="text-xs text-sky-700">Volume:</span>
                        <span className="text-base font-bold text-sky-800">
                          {Math.round(results.summary.bucket_stats?.bucket2?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-sky-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-sky-900">
                          {formatNumber(results.summary.bucket_stats?.bucket2?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-sky-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-sky-500" size={32} />
                </div>
              </div>

              {/* Bucket 3: q10/q90 Spreads */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-pink-600 font-medium">Bucket 3 Analysis</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">When Right:</span>
                        <span className="text-base font-bold text-pink-800">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.avg_profitable_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">When Wrong:</span>
                        <span className="text-base font-bold text-pink-800">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.avg_loss_spread || 0)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 pt-1 border-t border-pink-200">
                        <span className="text-xs text-pink-700">Volume:</span>
                        <span className="text-base font-bold text-pink-800">
                          {Math.round(results.summary.bucket_stats?.bucket3?.total_volume_mwh || 0)} MWh
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-pink-700">Profit/MWh:</span>
                        <span className="text-lg font-bold text-pink-900">
                          {formatNumber(results.summary.bucket_stats?.bucket3?.profit_per_mwh || 0)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-pink-600 mt-1">
                      Spread, volume & efficiency metrics
                    </p>
                  </div>
                  <Activity className="text-pink-500" size={32} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Results */}
        {results && results.results && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Daily Results
            </h2>
            <div className="space-y-2">
              {Object.entries(results.results)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, data]) => renderDateRow(date, data))}
            </div>

            {/* Hourly Performance Chart */}
            {hourlyPerformanceData.length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Hourly Performance Analysis
                </h3>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="hour"
                        label={{
                          value: 'Hour',
                          position: 'insideBottom',
                          offset: -5
                        }}
                      />
                      <YAxis
                        label={{
                          value: 'PnL',
                          angle: -90,
                          position: 'insideLeft'
                        }}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip formatter={(value) => formatNumber(value)} />
                      <ReferenceLine y={0} stroke="#666" />
                      <Bar dataKey="pnl">
                        {hourlyPerformanceData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Positive Hours</p>
                    <p className="text-xl font-bold text-green-700">
                      {hourlyPerformanceData.filter((d) => d.pnl > 0).length}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Negative Hours</p>
                    <p className="text-xl font-bold text-red-700">
                      {hourlyPerformanceData.filter((d) => d.pnl < 0).length}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProbabilisticModel;
